import type {
	CountryListItem,
	CountryOption,
	CountryStats,
	CreateCountryBody,
	ListCountriesQuery,
	Paginated,
	UpdateCountryBody,
} from "@seagames/contracts";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "../api/client";
import { queryClient } from "../api/query-client";
import { responsibleOrganizationsKeys } from "../responsible-organizations/queries";

export const countriesKeys = {
	all: ["countries"] as const,
	list: (q: ListCountriesQuery) => ["countries", "list", q] as const,
	options: ["countries", "options"] as const,
	stats: ["countries", "stats"] as const,
};

/** One request → page + total + every row's participant count (the API aggregates in SQL). */
export const countriesListOptions = (q: ListCountriesQuery) =>
	queryOptions({
		queryKey: countriesKeys.list(q),
		queryFn: ({ signal }) => api.get<Paginated<CountryListItem>>("/countries", { query: q, signal }),
		// Keep the previous page on screen while the next loads — no blank flash.
		placeholderData: keepPreviousData,
		staleTime: 60_000,
	});

/**
 * The whole list, for `<select>` elements elsewhere (participant registration,
 * nationality filters). Unpaginated by design — it is ISO-3166, it is 250 rows,
 * and it will still be 250 rows on the day the Games open. Cached hard: this
 * changes a handful of times in the lifetime of the platform.
 */
export const countryOptionsOptions = () =>
	queryOptions({
		queryKey: countriesKeys.options,
		queryFn: ({ signal }) => api.get<CountryOption[]>("/countries/options", { signal }),
		staleTime: 30 * 60_000,
	});

/** Also the responsible-organizations list, whose rows show `country_name`. (Countries are not in `/users/scope-options`.) */
export const invalidateCountries = () =>
	Promise.all([queryClient.invalidateQueries({ queryKey: countriesKeys.all }), queryClient.invalidateQueries({ queryKey: responsibleOrganizationsKeys.all })]);

export const createCountry = (body: CreateCountryBody) => api.post<CountryListItem>("/countries", body);

export const updateCountry = (id: string, body: UpdateCountryBody) =>
	api.patch<CountryListItem>(`/countries/${id}`, body);

/** `GET /countries/stats` — total, competing, reference only; one aggregate statement. */
export const countryStatsOptions = () =>
	queryOptions({
		queryKey: countriesKeys.stats,
		queryFn: ({ signal }) => api.get<CountryStats>("/countries/stats", { signal }),
		staleTime: 60_000,
	});
